<template>
    <div class="fill-height py-2">
        <v-data-table
            v-bind="$props"
            must-sort
            fixed-header
            hide-default-footer
            height="100%"
            class="elevation-4"
            :class="{ 'zebra-stripe': zebraStripping }"
            :show-select="showSelect"
            @update:sort-by="onUpdateSortBy"
            @update:sort-desc="onUpdateSortDesc"
        >
            <!-- ============= passing slots from parent to data-table ============ -->
            <template
                v-for="(_, name) in $scopedSlots"
                :slot="name"
                slot-scope="slotData"
            >
                <slot :name="name" v-bind="slotData"></slot>
            </template>
            <!-- ============= computedFields columns ============= -->
            <template
                v-for="field in computedFields"
                v-slot:[`item.${field.value}`]="{ item }"
            >
                <data-table-custom-field
                    :key="`custom-field-${field.value}`"
                    :field-name="field.value"
                    :computed-name="field.computed"
                    :computed-params="field.computedParams"
                    :row-data="item"
                >
                </data-table-custom-field>
            </template>
            <!-- ============== Row action buttons ================ -->
            <template v-slot:item.rowActions="{ item }">
                <slot :item="item" name="tableButtons"></slot>
            </template>
            <template v-slot:no-data>
                <h3>No data found...</h3>
            </template>
            <template v-slot:loading>
                <h3>Loading data... Please wait</h3>
            </template>
            <template v-if="allowExport" v-slot:header.rowActions>
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            icon
                            color="primary"
                            class="mx-2"
                            aria-label="Download data to Excel"
                            v-on="on"
                            @click="onExportClick"
                        >
                            <v-icon>{{ icons.download }}</v-icon>
                        </v-btn>
                    </template>
                    <span>Export data to csv</span>
                </v-tooltip>
            </template>
            <!-- =============== The select all checkbox ================== -->
            <template v-slot:header.data-table-select="{ props }">
                <v-checkbox
                    :value="props.value"
                    :indeterminate="props.indeterminate"
                    hide-details
                    class="mt-0"
                    :color="$vuetify.theme.isDark ? 'white' : 'grey darken-1'"
                    @change="onSelectAllChange"
                ></v-checkbox>
            </template>
            <!-- ================== the item selection box ================ -->
            <template v-slot:item.data-table-select="{ item, isSelected }">
                <v-checkbox
                    :value="isSelected"
                    hide-details
                    class="mt-0"
                    :color="$vuetify.theme.isDark ? 'white' : 'grey darken-1'"
                    @change="onItemSelect({ item, value: !isSelected })"
                ></v-checkbox>
            </template>
            <!-- =================== Bulk Actions Toolbar ============================ -->
            <template v-slot:top>
                <floating-toolbar
                    :show="selectedItems.length > 0"
                    :title="toolbarTitle"
                    :items="toolbarActions"
                    :toolbar-class="toolbarClass"
                    @cancel="onToolbarCancel"
                    @item-click="onToolbarClick"
                ></floating-toolbar>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import DataTableCustomField from '@/components/Fields/DataTableCustomField';
import FloatingToolbar from '@/components/Toolbars/FloatingToolbar';

export default {
    name: 'DataTableView',
    components: {
        'data-table-custom-field': DataTableCustomField,
        'floating-toolbar': FloatingToolbar
    },
    props: {
        headers: { type: Array, required: true },
        items: { type: Array, required: true },
        sortBy: { type: String, required: true },
        sortDesc: { type: Boolean, required: true },
        page: { type: Number, required: true },
        itemsPerPage: { type: Number, required: true },
        loading: { type: Boolean, required: true },
        zebraStripping: { type: Boolean, required: false, default: false },
        computedFields: { type: Array, required: false, default: () => [] },
        allowExport: { type: Boolean, required: false, default: false },
        showSelect: { type: Boolean, required: false, default: true },
        toolbarActions: { type: Array, required: false, default: () => [] },
        toolbarClass: { type: String, required: false, default: '' },
        value: { type: Array, requied: false, default: () => [] }
    },
    computed: {
        toolbarTitle() {
            return `${this.selectedItems.length} selected`;
        },
        selectedItems: {
            get() {
                return this.value;
            },
            set(newValue) {
                this.$emit('input', newValue);
            }
        }
    },
    watch: {
        items() {
            // need to reset the selectedItems
            this.selectedItems = [];
        }
    },
    methods: {
        // ===================== Events ========================
        onUpdateSortBy(value) {
            if (Array.isArray(value)) {
                if (value[0] && value[0] !== this.sortBy) {
                    this.$emit('update-sort-by', {
                        page: 1,
                        sortBy: value[0],
                        sortDesc: false
                    });
                }
            } else if (value && value !== this.sortBy) {
                this.$emit('update-sort-by', {
                    page: 1,
                    sortBy: value,
                    sortDesc: false
                });
            }
        },
        onUpdateSortDesc(value) {
            if (value !== undefined && value !== this.sortDesc) {
                this.$emit('update-sort-by', {
                    page: 1,
                    sortBy: this.sortBy,
                    sortDesc: value
                });
            }
        },
        onExportClick(event) {
            this.$emit('export', event);
        },
        onToolbarCancel() {
            this.selectedItems = [];
        },
        onToolbarClick({ item }) {
            this.$emit('toolbar', {
                item,
                selected: this.selectedItems
            });
        },
        onItemSelect({ item, value }) {
            if (value) {
                this.selectedItems = [...this.selectedItems, item];
            } else {
                this.selectedItems = this.selectedItems.filter(
                    elem => elem.id !== item.id
                );
            }
        },
        onSelectAllChange(value) {
            if (value) {
                this.selectedItems = [...this.items];
            } else {
                this.selectedItems = [];
            }
        }
    }
};
</script>

<style lang="scss" scoped>
button.data-export {
    z-index: 10;
}
</style>
